import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Brain, Users } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function AddCoachRating() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [formData, setFormData] = useState({
    rating_date: new Date().toISOString().split('T')[0],
    confidence: 5,
    commitment: 5,
    mental_control: 5,
    concentration: 5,
    enjoying_training: 5,
    helping_others: 5,
    communication: 5,
    involvement: 5,
    coach_name: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadChild = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}`);
      setChild(response.data);
    } catch (error) {
      toast.error('Failed to load child data');
      console.error(error);
    }
  }, [childId]);

  useEffect(() => {
    loadChild();
  }, [loadChild]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        rating_date: new Date(formData.rating_date).toISOString(),
        confidence: parseInt(formData.confidence),
        commitment: parseInt(formData.commitment),
        mental_control: parseInt(formData.mental_control),
        concentration: parseInt(formData.concentration),
        enjoying_training: parseInt(formData.enjoying_training),
        helping_others: parseInt(formData.helping_others),
        communication: parseInt(formData.communication),
        involvement: parseInt(formData.involvement)
      };

      await axios.post(`${API}/children/${childId}/coach-ratings`, payload);
      toast.success('Coach rating saved successfully!');
      navigate(`/child/${childId}`);
    } catch (error) {
      toast.error('Failed to save rating');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const RatingSlider = ({ label, name, value, description }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-300">{label}</Label>
        <span className="text-2xl font-bold text-[#02b5ff]">{value}/10</span>
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      <input
        type="range"
        min="1"
        max="10"
        value={value}
        onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer slider"
        style={{
          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(value - 1) * 11.11}%, #E5E7EB ${(value - 1) * 11.11}%, #E5E7EB 100%)`
        }}
      />
      <div className="flex justify-between text-xs text-gray-400">
        <span>Low</span>
        <span>High</span>
      </div>
    </div>
  );

  if (!child) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-gray-400 hover:text-white mb-4 rounded-lg"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile
          </Button>
          <h1 className="text-4xl font-bold text-white" data-testid="add-rating-title">
            Coach Rating
          </h1>
          <p className="text-gray-400 mt-2">
            Rate {child.first_name} {child.last_name}'s psychological traits and social interaction
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Rating Date *
                </Label>
                <Input
                  type="date"
                  required
                  value={formData.rating_date}
                  onChange={(e) => setFormData({ ...formData, rating_date: e.target.value })}
                  className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Coach Name
                </Label>
                <Input
                  value={formData.coach_name}
                  onChange={(e) => setFormData({ ...formData, coach_name: e.target.value })}
                  className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                  placeholder="Your name"
                />
              </div>
            </div>
          </div>

          {/* Psychological Traits */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                <Brain className="h-6 w-6 text-[#02b5ff]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Psychological Traits</h2>
                <p className="text-sm text-gray-400">Mental and emotional attributes</p>
              </div>
            </div>

            <div className="space-y-6">
              <RatingSlider
                label="1. Confidence"
                name="confidence"
                value={formData.confidence}
                description="Self-belief in abilities and decision-making"
              />
              <RatingSlider
                label="2. Commitment"
                name="commitment"
                value={formData.commitment}
                description="Dedication and consistency in training and goals"
              />
              <RatingSlider
                label="3. Mental Control"
                name="mental_control"
                value={formData.mental_control}
                description="Ability to manage emotions and stay composed under pressure"
              />
              <RatingSlider
                label="4. Concentration"
                name="concentration"
                value={formData.concentration}
                description="Focus and attention span during activities"
              />
            </div>
          </div>

          {/* Social Interaction */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-[#02b5ff]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Social Interaction</h2>
                <p className="text-sm text-gray-400">Teamwork and interpersonal skills</p>
              </div>
            </div>

            <div className="space-y-6">
              <RatingSlider
                label="1. Enjoying Training and Games"
                name="enjoying_training"
                value={formData.enjoying_training}
                description="Enthusiasm and positive attitude during sessions"
              />
              <RatingSlider
                label="2. Helping Others"
                name="helping_others"
                value={formData.helping_others}
                description="Willingness to support and assist teammates"
              />
              <RatingSlider
                label="3. Communication"
                name="communication"
                value={formData.communication}
                description="Ability to express ideas, answer questions, and participate in discussions"
              />
              <RatingSlider
                label="4. Involvement"
                name="involvement"
                value={formData.involvement}
                description="Active participation and engagement in activities"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <Label className="text-sm font-semibold text-gray-300 mb-2 block">
              Additional Notes
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="min-h-32 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
              placeholder="Any specific observations, strengths, or areas for development..."
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate(`/child/${childId}`)}
              className="h-12 px-8 bg-gray-100 hover:bg-gray-200 text-gray-300 font-semibold rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-lg flex-1"
            >
              <Save className="mr-2 h-5 w-5" />
              {submitting ? 'Saving...' : 'Save Rating'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
